/**
 * Transfer history tab — renders transfer records with Source and Destination
 * details (Location, Branch, Department, Campaign, Team, Agent) matching the
 * KC2 transfer tab layout.
 */
import { useState } from 'react'
import type { JSX } from 'react'
import { ArrowRight, ChevronDown, ChevronRight, Lock, LockOpen } from 'lucide-react'
import type { TransferRecord } from './useConversationTransfers.js'
import { formatDateTime } from '../../../utils/timezone.js'
import { R } from './primitives.js'
import { SafeHtml } from '../../common/SafeHtml.js'

function DetailRow({ label, value }: { label: string; value: string | null | undefined }): JSX.Element {
  return (
    <p className="text-xs text-slate-600">
      <span className="font-medium text-slate-500">{label} : </span>
      <span className={value ? 'text-slate-800' : 'text-slate-300'}>{value || '-'}</span>
    </p>
  )
}

function TransferCard({ record }: { record: TransferRecord }): JSX.Element {
  const [expanded, setExpanded] = useState(false)
  const srcAgent = record.source.agentName ?? '-'
  const dstAgent = record.destination.agentName ?? (record.destination.teamName ? 'Queue' : '-')

  return (
    <div className={`rounded-[3px] border border-slate-200 bg-white ${R} shadow-sm`}>
      {/* Accordion header — clickable */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-slate-50/50"
      >
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[3px] bg-[#F5F7FF] text-[#3366FF]">
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-800">
              Transferred from {srcAgent} to {dstAgent}
            </p>
            <p className="text-[11px] text-slate-400">{formatDateTime(record.transferredAt)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {record.transferType === 'automatic' ? (
            <span className="rounded-[3px] bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-600">
              Automatic
            </span>
          ) : record.isPrivate ? (
            <span className="flex items-center gap-1 text-[11px] text-slate-400">
              <Lock className="h-3 w-3" /> Private
            </span>
          ) : (
            <span className="flex items-center gap-1 text-[11px] text-slate-400">
              <LockOpen className="h-3 w-3" /> Public
            </span>
          )}
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
          )}
        </div>
      </button>

      {/* Accordion body — collapsible */}
      {expanded && (
        <>
          {/* Source & Destination */}
          <div className="grid grid-cols-1 gap-4 border-t border-slate-100 px-4 py-3 sm:grid-cols-2">
            {/* Source */}
            <div className={`rounded-[3px] bg-slate-50/70 p-3 ${R}`}>
              <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Source</h4>
              <div className="space-y-0.5">
                <DetailRow label="Location" value={record.source.locationName} />
                <DetailRow label="Branch" value={record.source.branchName} />
                <DetailRow label="Department" value={record.source.departmentName} />
                <DetailRow label="Campaign" value={record.source.campaignName} />
                <DetailRow label="Team" value={record.source.teamName} />
                <DetailRow label="Agent" value={record.source.agentName} />
              </div>
            </div>

            {/* Destination */}
            <div className={`rounded-[3px] bg-[#F5F7FF]/50 p-3 ${R}`}>
              <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-[#3366FF]/70">Destination</h4>
              <div className="space-y-0.5">
                <DetailRow label="Location" value={record.destination.locationName} />
                <DetailRow label="Branch" value={record.destination.branchName} />
                <DetailRow label="Department" value={record.destination.departmentName} />
                <DetailRow label="Campaign" value={record.destination.campaignName} />
                <DetailRow label="Team" value={record.destination.teamName} />
                <DetailRow label="Agent" value={record.destination.agentName} />
              </div>
            </div>
          </div>

          {/* CC emails */}
          {record.cc.length > 0 && (
            <div className="border-t border-slate-100 px-4 py-2">
              <h4 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Email CC</h4>
              <p className="text-xs text-slate-600">{record.cc.join(', ')}</p>
            </div>
          )}

          {/* Transfer metadata */}
          {(record.transferType || record.reason) && (
            <div className="border-t border-slate-100 px-4 py-2">
              <h4 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Transfer Details</h4>
              <div className="space-y-0.5">
                {record.transferType && (
                  <p className="text-xs text-slate-600">
                    <span className="font-medium text-slate-500">Type : </span>
                    <span className="capitalize">{record.transferType}</span>
                  </p>
                )}
                {record.reason && (
                  <p className="text-xs text-slate-600">
                    <span className="font-medium text-slate-500">Reason : </span>
                    <span>{record.reason}</span>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {record.description && (
            <div className="border-t border-slate-100 px-4 py-2">
              <h4 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Description</h4>
              <SafeHtml
                className="text-xs text-slate-600 [&_p]:my-0"
                html={record.description}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default function TransferHistoryTab({
  transfers,
  loading,
}: {
  transfers: TransferRecord[]
  loading: boolean
}): JSX.Element {
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-slate-400">
        Loading transfers…
      </div>
    )
  }

  if (transfers.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-slate-400">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-blue-50 text-[#3366FF]/70">
          <ArrowRight className="h-9 w-9" strokeWidth={1.5} />
        </div>
        <p className="text-sm font-semibold text-slate-500">No transfers</p>
        <p className="max-w-xs text-xs text-slate-400">
          This conversation has not been transferred yet.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {transfers.map((t) => (
        <TransferCard key={t.id} record={t} />
      ))}
    </div>
  )
}
